import React from 'react';
import PropTypes from 'prop-types';
import { Paper } from '@material-ui/core';
import { Table } from '@knockrentals/knock-shared-web-v2';
import Pagination from './Pagination';
import PaginationMessage from './PaginationMessage';
import { usePaginatedDataTable } from './use-paginated-data-table';
import { makeCss } from '../../make-css';

const useCss = makeCss({
  outlinedPaper: {
    display: 'flex',
    flex: 1,
    flexDirection: 'column',
    justifyContent: 'space-between',
    minHeight: 0,
    alignItems: 'stretch',
    overflow: 'hidden',
    '& table th, table td': {
      minWidth: 'unset',
      maxWidth: 'unset',
    },
    '& .MuiTableContainer-root': {
      flex: 1,
    },
  },
});

const PaginatedDataTable = ({
  className,
  columns,
  columnRenderers,
  data,
  dataRowId,
  defaultRowsPerPage,
  defaultSortColumn,
  errorFallback,
  getTableData,
  isLoading,
  onSelectRow,
  paginationMessage,
  rowsPerPageOptions,
  selectedRows,
  totalDataRowCount,
  ...rest
}) => {
  const css = useCss();
  const {
    handlePageOnChange,
    handleRowsPerPageOnChange,
    handleSortOnChange,
    page,
    rowsPerPage,
    sortColumn,
    sortDirection,
  } = usePaginatedDataTable({
    defaultRowsPerPage,
    defaultSortColumn,
    getTableData,
    onSelectRow,
  });

  return (
    <Paper className={className} css={css.outlinedPaper} variant="outlined">
      <Table
        columns={columns}
        columnRenderers={columnRenderers}
        errorFallback={errorFallback}
        isLoading={isLoading}
        onSelect={onSelectRow}
        rows={data}
        rowId={dataRowId}
        selectedRows={selectedRows}
        sortBy={sortColumn}
        sortDirection={sortDirection}
        onSortChange={handleSortOnChange}
        {...rest}
      />
      <Pagination
        count={totalDataRowCount}
        onPageChange={handlePageOnChange}
        onRowsPerPageChange={handleRowsPerPageOnChange}
        page={page}
        rowsPerPage={rowsPerPage}
        rowsPerPageOptions={rowsPerPageOptions}
      >
        {paginationMessage && <PaginationMessage message={paginationMessage} />}
      </Pagination>
    </Paper>
  );
};

PaginatedDataTable.propTypes = {
  className: PropTypes.string,
  columns: PropTypes.arrayOf(PropTypes.object),
  columnRenderers: PropTypes.object,
  data: PropTypes.arrayOf(PropTypes.object),
  dataRowId: PropTypes.string,
  defaultRowsPerPage: PropTypes.number,
  defaultSortColumn: PropTypes.string,
  errorFallback: PropTypes.node,
  getTableData: PropTypes.func,
  isError: PropTypes.bool,
  isLoading: PropTypes.bool,
  onSelectRow: PropTypes.func,
  paginationMessage: PropTypes.string,
  rowsPerPageOptions: PropTypes.arrayOf(PropTypes.number),
  selectedRows: PropTypes.array,
  totalDataRowCount: PropTypes.number,
};

export default PaginatedDataTable;
